import { select, coerceArray } from '@ngneat/elf';
import { pipe, distinctUntilKeyChanged, EMPTY, defer, combineLatest, map as map$1, take, switchMap, of, filter, throwError, tap as tap$1, BehaviorSubject } from 'rxjs';
import { distinctUntilKeyChanged as distinctUntilKeyChanged$1, tap, map, distinctUntilChanged } from 'rxjs/operators';

function withRequestsCache(initialValue) {
  return {
    props: {
      requestsCache: initialValue ?? {}
    },
    config: undefined
  };
}
function selectRequestCache(key) {
  return pipe(distinctUntilKeyChanged('requestsCache'), select(state => getRequestCache(key)(state)));
}
function updateRequestsCache(requestsOrKeys, value) {
  let normalized = requestsOrKeys;
  if (value) {
    normalized = requestsOrKeys.reduce((acc, key) => {
      acc[key] = value;
      return acc;
    }, {});
  }
  return function (state) {
    return {
      ...state,
      requestsCache: {
        ...state.requestsCache,
        ...normalized
      }
    };
  };
}
function updateRequestCache(key, {
  ttl,
  value: v
} = {}) {
  const data = {
    value: v ?? 'full'
  };
  if (ttl) {
    data.timestamp = Date.now() + ttl;
  }
  return function (state) {
    return {
      ...state,
      requestsCache: {
        ...state.requestsCache,
        [key]: data
      }
    };
  };
}
function getRequestCache(key) {
  return function (state) {
    const cacheValue = state.requestsCache[key] ?? {
      value: 'none'
    };
    if (cacheValue.timestamp && cacheValue.timestamp < Date.now()) {
      return {
        value: 'none'
      };
    }
    return cacheValue;
  };
}
function selectIsRequestCached(key, options) {
  return pipe(distinctUntilKeyChanged('requestsCache'), select(state => isRequestCached(key, options)(state)));
}
function isRequestCached(key, options) {
  return function (state) {
    const type = options?.value ?? 'full';
    return coerceArray(key).some(k => getRequestCache(k)(state).value === type);
  };
}
function skipWhileCached(store, key, options) {
  return function (source) {
    if (store.query(isRequestCached(key, {
      value: options?.value
    }))) {
      return options?.returnSource ?? EMPTY;
    }
    return source;
  };
}
function createRequestsCacheOperator(store) {
  return function (key, options) {
    return skipWhileCached(store, key, options);
  };
}
function clearRequestsCache() {
  return function (state) {
    return {
      ...state,
      requestsCache: {}
    };
  };
}

function withRequestsStatus(initialValue) {
  return {
    props: {
      requestsStatus: initialValue ?? {}
    },
    config: undefined
  };
}
function updateRequestsStatus(requestsOrKeys, value, error) {
  let normalized = requestsOrKeys;
  if (value) {
    normalized = requestsOrKeys.reduce((acc, key) => {
      acc[key] = resolveStatus(value, error);
      return acc;
    }, {});
  }
  return function (state) {
    return {
      ...state,
      requestsStatus: {
        ...state.requestsStatus,
        ...normalized
      }
    };
  };
}
function resolveStatus(value, error) {
  const newStatus = {
    value
  };
  if (value === 'error') {
    newStatus.error = error;
  }
  return newStatus;
}
function updateRequestStatus(key, value, error) {
  return function (state) {
    return {
      ...state,
      requestsStatus: {
        ...state.requestsStatus,
        [key]: resolveStatus(value, error)
      }
    };
  };
}
function getRequestStatus(key) {
  return function (state) {
    return state.requestsStatus[key] ?? {
      value: 'idle'
    };
  };
}
function selectRequestStatus(key, options) {
  return pipe(distinctUntilKeyChanged$1('requestsStatus'), select(state => {
    const base = getRequestStatus(key)(state);
    if (options?.groupKey) {
      const parent = getRequestStatus(options.groupKey)(state);
      return parent.value === 'success' ? parent : base;
    }
    return base;
  }));
}
function selectIsRequestPending(key) {
  return pipe(distinctUntilKeyChanged$1('requestsStatus'), select(state => getRequestStatus(key)(state).value === 'pending'));
}
function trackRequestStatus(store, key, options) {
  return function (source) {
    return defer(() => {
      if (store.query(getRequestStatus(key)).value !== 'pending') {
        store.update(updateRequestStatus(key, 'pending'));
      }
      return source.pipe(tap({
        error(error) {
          store.update(updateRequestStatus(key, 'error', options?.mapError ? options?.mapError(error) : error));
        }
      }));
    });
  };
}
function createRequestsStatusOperator(store) {
  return function (key, options) {
    return trackRequestStatus(store, key, options);
  };
}
function initializeAsPending(keys) {
  return coerceArray(keys).reduce((acc, key) => {
    acc[key] = {
      value: 'pending'
    };
    return acc;
  }, {});
}
function clearRequestsStatus() {
  return function (state) {
    return {
      ...state,
      requestsStatus: {}
    };
  };
}

function createRequestDataSource(params) {
  const {
    data$,
    store,
    dataKey,
    requestStatusOptions,
    requestKey,
    idleAsPending = false
  } = Reflect.has(params, 'requestKey') ? params : {
    ...params,
    requestKey: undefined
  };
  return {
    trackRequestStatus: options => trackRequestStatus(store, requestKey ?? options?.key, options),
    skipWhileCached: options => skipWhileCached(store, requestKey ?? options?.key, options),
    setSuccess(options) {
      return updateRequestStatus(requestKey ?? options?.key, 'success');
    },
    setCached(options) {
      return updateRequestCache(requestKey ?? options?.key, options);
    },
    data$: options => store.combine({
      data: data$(requestKey ?? options?.key),
      status: store.pipe(selectRequestStatus(requestKey ?? options?.key, requestStatusOptions || {}))
    }).pipe(map(({
      data,
      status
    }) => {
      return {
        [dataKey]: data,
        loading: idleAsPending ? status.value === 'pending' || status.value === 'idle' : status.value === 'pending',
        error: status.value === 'error' ? status.error : undefined
      };
    }), distinctUntilChanged((a, b) => {
      // if the status is the same, for example, `pending` and `pending`, and the `data` is the same
      // don't emit a redundant value
      return a[dataKey] === b[dataKey] && a.loading === b.loading && a.error === b.error;
    }))
  };
}

function initialResult() {
  return {
    isError: false,
    isLoading: true,
    isSuccess: false,
    status: 'loading',
    fetchStatus: 'idle',
    successfulRequestsCount: 0
  };
}
function getSource(key, initialValue, map) {
  const item = map.get(resolveKey(key));
  if (item) {
    return item.asObservable();
  }
  const newSource = new BehaviorSubject(initialValue);
  map.set(resolveKey(key), newSource);
  return newSource.asObservable();
}
function resolveKey(key) {
  return JSON.stringify(key);
}
const emitters = new Map();
// @public
function getRequestResult(key, {
  initialStatus
} = {}) {
  let result = initialResult();
  if (initialStatus === 'idle') {
    result = {
      ...result,
      status: 'idle',
      isLoading: false
    };
  }
  return getSource(key, result, emitters);
}
function updateRequestResult(key, newValue) {
  const result = emitters.get(resolveKey(key));
  if (result) {
    let hasChange = false;
    const currentResult = result.getValue();
    for (const key of Object.keys(newValue)) {
      if (currentResult[key] !== newValue[key]) {
        hasChange = true;
        break;
      }
    }
    hasChange && result.next({
      ...currentResult,
      ...newValue
    });
  }
}
// @public
function clearRequestsResult() {
  emitters.clear();
}
// @public
function deleteRequestResult(key) {
  emitters.delete(resolveKey(key));
}
// @public
function resetStaleTime(key) {
  updateRequestResult(key, {
    staleTime: undefined
  });
}
// @public
function joinRequestResult(...[key, options]) {
  return function (source) {
    return combineLatest([source, getRequestResult(key, options)]).pipe(map$1(([data, result]) => {
      return {
        ...result,
        data
      };
    }));
  };
}
function trackRequestResult(key, options) {
  return function (source) {
    return getRequestResult(key).pipe(take(1), switchMap(result => {
      const stale = options?.staleTime ? result.staleTime < Date.now() : false;
      const preventConcurrentRequest = options?.preventConcurrentRequest !== undefined ? options.preventConcurrentRequest : true;
      const cacheResponseData = options?.cacheResponseData;
      if (result.isSuccess && !options?.skipCache && !stale) {
        return of(result.responseData);
      }
      if (result.fetchStatus === 'fetching' && preventConcurrentRequest && !options?.skipCache && !stale) {
        return getRequestResult(key).pipe(filter(requestResult => requestResult.fetchStatus === 'idle'), switchMap(requestResult => {
          if (requestResult.isError) {
            return throwError(() => requestResult.error);
          }
          if (requestResult.isSuccess) {
            return of(requestResult.responseData);
          }
          return EMPTY;
        }), take(1));
      }
      updateRequestResult(key, {
        isLoading: true,
        isError: false,
        isSuccess: false,
        status: 'loading',
        fetchStatus: 'fetching'
      });
      let sourceData;
      return source.pipe(tap$1({
        error(error) {
          updateRequestResult(key, {
            isError: true,
            isLoading: false,
            isSuccess: false,
            status: 'error',
            fetchStatus: 'idle',
            errorUpdatedAt: Date.now(),
            error
          });
        },
        next(data) {
          sourceData = data;
        },
        complete() {
          const newResult = {
            isLoading: false,
            isSuccess: true,
            isError: false,
            status: 'success',
            fetchStatus: 'idle',
            responseData: cacheResponseData ? sourceData : undefined,
            dataUpdatedAt: Date.now(),
            successfulRequestsCount: result.successfulRequestsCount + 1
          };
          if (options?.staleTime) {
            newResult.staleTime = Date.now() + options.staleTime;
          }
          updateRequestResult(key, newResult);
          if (options?.additionalKeys) {
            const mainKey = resolveKey(key);
            for (const keys of options.additionalKeys(sourceData)) {
              emitters.set(resolveKey(keys), emitters.get(mainKey));
            }
          }
        }
      }));
    }));
  };
}
function filterSuccess() {
  return filter(result => result.status === 'success');
}
function filterError() {
  return filter(result => result.status === 'error');
}
function mapResultData(mapFn) {
  return pipe(map$1(result => {
    return {
      ...result,
      data: result.data != null ? mapFn(result.data) : result.data
    };
  }));
}

export { clearRequestsCache, clearRequestsResult, clearRequestsStatus, createRequestDataSource, createRequestsCacheOperator, createRequestsStatusOperator, deleteRequestResult, filterError, filterSuccess, getRequestCache, getRequestResult, getRequestStatus, initializeAsPending, isRequestCached, joinRequestResult, mapResultData, resetStaleTime, selectIsRequestCached, selectIsRequestPending, selectRequestCache, selectRequestStatus, trackRequestResult, updateRequestCache, updateRequestStatus, updateRequestsCache, updateRequestsStatus, withRequestsCache, withRequestsStatus };
